import { Component, type ErrorInfo, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { logFrontendError } from "@/lib/errorLogging";

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Catches render-time crashes (e.g. a broken provider/dispatcher) so the app
 * shows a recoverable screen instead of a blank page, and reports them.
 */
export class AppErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    void logFrontendError({
      errorType: "react",
      message: error.message,
      stack: [error.stack, info.componentStack].filter(Boolean).join("\n\n"),
    });
  }

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="min-h-screen bg-background text-foreground flex items-center justify-center p-6">
        <div className="max-w-md space-y-4 text-center">
          <h1 className="font-display text-2xl font-bold">Something broke on this page</h1>
          <p className="text-sm text-muted-foreground">
            The error has been reported. Reloading usually fixes it.
          </p>
          <p className="font-mono text-xs text-muted-foreground break-words">
            {this.state.error.message}
          </p>
          <Button onClick={() => window.location.reload()}>Reload page</Button>
        </div>
      </div>
    );
  }
}

export default AppErrorBoundary;
